import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';
import { BillingStatus } from './projects';

/**
 * Row shape for the org-level billing overview (TELV1-524). Distinct from
 * `Project` in `./projects` — this comes from a separate aggregation
 * (`/billing/overview`) that mixes in organization/workspace lookups and a
 * per-project completed-conversation count, and keeps the backend's native
 * `respondents_max` (snake_case) instead of the camelCase the `/projects`
 * list normalizes to.
 */
export interface BillingOverviewRow {
  _id: string;
  title: string;
  status: string;
  createdAt: string;
  respondents_max?: number;
  billingStatus: BillingStatus | null;
  billingAmount: number | null;
  billingCurrency: string | null;
  billingReference: string | null;
  billingNotes: string | null;
  billingStatusUpdatedAt: string | null;
  billingInvoicedSnapshot: { completedCount: number; at: string } | null;
  hubspotDealId: string | null;
  workspaceName: string | null;
  organizationId: string | null;
  organizationName: string | null;
  organizationHubspotCompanyId: string | null;
  completedCount: number;
}

/** `NEW` is the filter value for "no billing status set" (`billingStatus: null`). */
export type BillingOverviewStatusFilter = BillingStatus | 'NEW';

export interface BillingOverviewFilters {
  page?: number;
  limit?: number;
  search?: string;
  organization_id?: string;
  billingStatus?: BillingOverviewStatusFilter;
  /** Only projects of organizations with an active (paid) subscription. */
  subscribed?: boolean;
  sortBy?: 'title' | 'createdAt' | 'billingStatus';
  sortOrder?: 'asc' | 'desc';
}

export type BillingExportFileType = 'csv' | 'xlsx';

export interface OrganizationHubspotLink {
  hubspotCompanyId: string | null;
  hubspotLinkedAt: string | null;
  hubspotLinkedBy: string | null;
}

export interface UpdateOrganizationHubspotLinkPayload {
  hubspotCompanyId: string | null;
}

export interface HubspotStatus {
  configured: boolean;
  portalId: string | null;
}

export interface HubspotCompanyHit {
  id: string;
  name: string | null;
  domain: string | null;
}

export interface HubspotDealHit {
  id: string;
  dealname: string | null;
  dealstage: string | null;
}

export interface HubspotSearchResponse<T> {
  configured: boolean;
  results: T[];
}

export const billingApi = {
  /**
   * Org-level billing overview across all projects
   */
  getOverview: async (
    params: BillingOverviewFilters,
  ): Promise<PaginatedResponse<BillingOverviewRow>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<BillingOverviewRow>>(
      '/billing/overview',
      { params },
    );
    return data;
  },

  /**
   * Export the billing overview (current filters applied) as CSV or XLSX
   */
  exportOverview: async (
    params: BillingOverviewFilters,
    fileType: BillingExportFileType,
  ): Promise<Blob> => {
    const { data } = await axiosInstance.get<Blob>('/billing/overview/export', {
      params: { ...params, file_type: fileType },
      responseType: 'blob',
    });
    return data;
  },

  /**
   * Get an organization's HubSpot company link
   */
  getOrganizationHubspotLink: async (organizationId: string): Promise<OrganizationHubspotLink> => {
    const { data } = await axiosInstance.get<OrganizationHubspotLink>(
      `/organizations/${organizationId}/hubspot`,
    );
    return data;
  },

  /**
   * Link, change, or unlink (hubspotCompanyId: null) an organization's HubSpot company
   */
  updateOrganizationHubspotLink: async (
    organizationId: string,
    payload: UpdateOrganizationHubspotLinkPayload,
  ): Promise<OrganizationHubspotLink> => {
    const { data } = await axiosInstance.patch<OrganizationHubspotLink>(
      `/organizations/${organizationId}/hubspot`,
      payload,
    );
    return data;
  },

  /**
   * Whether HubSpot lookups are configured, plus the portal id for deep links
   */
  getHubspotStatus: async (): Promise<HubspotStatus> => {
    const { data } = await axiosInstance.get<HubspotStatus>('/hubspot/status');
    return data;
  },

  /**
   * Search HubSpot companies (linking assist). Backend requires q >= 3 chars.
   */
  searchHubspotCompanies: async (q: string): Promise<HubspotSearchResponse<HubspotCompanyHit>> => {
    const { data } = await axiosInstance.get<HubspotSearchResponse<HubspotCompanyHit>>(
      '/hubspot/companies',
      { params: { q } },
    );
    return data;
  },

  /**
   * Search HubSpot deals (linking assist). Backend requires q >= 3 chars.
   */
  searchHubspotDeals: async (q: string): Promise<HubspotSearchResponse<HubspotDealHit>> => {
    const { data } = await axiosInstance.get<HubspotSearchResponse<HubspotDealHit>>(
      '/hubspot/deals',
      { params: { q } },
    );
    return data;
  },
};
